import React, { useMemo, useEffect, useState, useCallback } from "react";

import { Alert } from "services/Alert";

import { useWindowWidth } from "hooks";
import {
    ROUTING_CREATE_API, ROUTING_MODIFY_API, ROUTING_REMOVE_API
} from "const/apiMethods";
import { hasErrorObject } from "utils";
import { checkPermissionsFor } from "store/storeHelpers";
import {
    getRoutingList, onCreateRoutingItem, onModifyRoutingItem, onRemoveRoutingItem,
    getRoutingSupplierList
} from "actions/routing";
import { getReferencesForReports } from "actions/references";
import { getE212DropdownList } from "actions/e212";
import PageHeader from "components/base/PageHeader";
import PanelLayout from "components/base/PanelLayout";
import RoutingTable from "./RoutingTable";
import RoutingFilter from "./RoutingFilter";
import "./Routing.css"
import { connect } from "react-redux";
import { Schema } from "rsuite";

const { StringType, ArrayType } = Schema.Types;

const createFormDefaultData = {
    active: true,
    permanent: false,
    lock_to_reliable: false,
    type: "blocked"
};


const RoutingPage = ({
    loading,
    e212List,
    routingList,
    dialerList,
    supplierList,
    reportLoading,
    supplierAllManaged,

    getRoutingList,
    createApiFunc,
    modifyApiFunc,
    removeApiFunc,
    getE212DropdownList,
    getReferencesForReports,
    getRoutingSupplierList
}) => {
    const defaultFilter = {
        type: null,
        supplier_id: null,
        senderid: "",
        mcc_mnc: "",
        mcc_mnc_match_like: null,
        dialer_id_list: [],
        min_rate: ""
    };

    const [filter, onChangeFilter] = useState(defaultFilter);
    const [editKeys, setEditKeys] = useState([]);
    const [activeRoutes, setActiveRoutes] = useState(new Map());
    const [noAutoDeleteRoutes, setNoAutoDeleteRoutes] = useState(new Map());
    const [lockToTrustedRoutes, setLockToTrustedRoutes] = useState(new Map());
    const [createState, setCreateState] = useState(createFormDefaultData);

    const createFormModel = useMemo(() => {
        const showOnLocked = createState.type === "locked";
        const showOnNotLockedTrusted = !createState.lock_to_reliable;
        // const showSupplier = createState.type === "blocked";

        return Schema.Model({
            senderid: StringType()
                .minLength(1, "Required minimum 1 character")
                .maxLength(40, "The maximum is only 40 character"),
            mcc_mnc: StringType()
                .pattern(/^[0-9][\d]*$/, "The mcc and mcc must contain only digits")
                .maxLength(6, "The maximum is 6 characters"),
            comment: StringType()
                .minLength(1, "Required minimum 1 character")
                .maxLength(255, "The maximum is only 255 character"),
            ...(!supplierAllManaged || showOnLocked ? {
                supplier_id: StringType()
                    .isRequired("Please select a supplier")
            } : {}),
            ...(showOnLocked && showOnNotLockedTrusted ? {
                dialer_id_list: ArrayType()
                    .isRequired("Dialers required for locked rules")
            } : {})
        });
    }, [createState, supplierAllManaged]);

    const modifyFormModel = useCallback((data) => {
        const showOnLocked = data.type === "locked";
        const lockedToTrusted = data.lock_to_reliable;

        return Schema.Model({
            ...(!supplierAllManaged || showOnLocked ? {
                supplier_id: StringType()
                    .isRequired("Please select a supplier")
            } : {}),
            ...(showOnLocked && !lockedToTrusted ? {
                dialer_id_list: ArrayType()
                    .isRequired("Dialers required for locked rules")
            } : {})
        });
    }, [supplierAllManaged]);


    const mccMncList = e212List && e212List.length ? e212List.map(value => {
        return {
            ...value,
            nameMccMnc: `${value.mccmnc_name} (${value.mcc_mnc})`
        };
    }) : [];

    const data = useMemo(() => (routingList || []).map((item) => ({
        ...item,
        dialer_id_list: (item.dialer_list || []).map(dialer => dialer.dialer_id)
    })), [routingList]);

    const routingFilteredList = useMemo(() => {
        if (!data)
            return [];

        const toLower = (value) => (value ?? "").toString().toLowerCase().trim();

        const filterSender = toLower(filter.senderid);
        const filterMccMnc = toLower(filter.mcc_mnc);


        const filteredData = data.filter(item => {
            const dialerIdList = item.dialer_list
                ? item.dialer_list.map((i) => String(i.dialer_id))
                : [];

            if (filter.type && filter.type !== item.type) {
                return false;
            }

            if (
                filter.min_rate !== null &&
                (filter.min_rate > item.min_rate || item.min_rate === null)
            ) {
                return false;
            }

            if (filter.supplier_id && filter.supplier_id !== item.supplier_id) {
                return false;
            }

            if (
                filter.dialer_id != null &&
                dialerIdList.length &&
                !dialerIdList.includes(String(filter.dialer_id))
            ) {
                return false;
            }

            if (filterSender) {
                const itemSender = toLower(item.senderid);
                if (!itemSender) return false;
                if (!itemSender.includes(filterSender)) return false;
            }

            if (filterMccMnc) {
                const itemMccMnc = toLower(item.mcc_mnc);
                if (!itemMccMnc) return false;
                if (!itemMccMnc.includes(filterMccMnc)) return false;
            }

            if (filter.mcc_mnc_match_like !== null && filter.mcc_mnc_match_like !== item.mcc_mnc_match_like) {
                return false;
            }

            return true;
        });

        return filteredData;
    }, [data, filter]);

    useEffect(() => {
        getRoutingList();
        getRoutingSupplierList();
        getReferencesForReports();
        getE212DropdownList({}, true);
    }, []);


    const onDataUpdate = () => {
        getRoutingList();
    };

    const handleCreateException = async (params) => {
        const {
            supplier_id: supplierId,
            dialer_id_list: dialerIdList,
            supplier_trunk_id: supplierTrunkId,
            ...restParams
        } = params;

        const currentSupplier = supplierList.find(item => item.supplier_id === supplierId);

        const currentSupplierTrunkList = currentSupplier ? currentSupplier.trunk_list : [];
        const trunkIsActual = currentSupplierTrunkList.find(item => item.id === supplierTrunkId);

        const finalParams = {
            target: {
                ...(supplierAllManaged && !supplierId ? {} : { "sms.supplier_id": supplierId }),
                "sms.supplier_trunk_id": trunkIsActual ? supplierTrunkId : null,
                dialer_id_list: dialerIdList
            },
            ...restParams,
            senderid: restParams?.senderid ? restParams.senderid : "",
            comment: restParams?.comment ? restParams.comment : "",
            active: restParams.active,
            permanent: restParams.permanent,
            lock_to_reliable: restParams.lock_to_reliable
        };

        try {
            const response = await createApiFunc(finalParams);

            if (response && response.routing_key) {
                Alert.success("Routing created!");
            }

            return response;
        } catch (error) {
            throw error;
        }
    };

    const handleModifyException = async (params, rowData) => {
        const {
            supplier_id: supplierId,
            dialer_id_list: dialerIdList,
            supplier_trunk_id: supplierTrunkId,
            ...restParams
        } = params;

        const currentSupplier = supplierList.find(item => item.supplier_id === supplierId);
        const currentSupplierTrunkList = currentSupplier ? currentSupplier.trunk_list : [];
        const trunkIsActual = currentSupplierTrunkList.find(item => item.id === supplierTrunkId);

        const currentActive = activeRoutes.get(rowData.routing_key);
        const currentRemoveInactive = noAutoDeleteRoutes.get(rowData.routing_key);
        const currentLockToTrusted = lockToTrustedRoutes.get(rowData.routing_key);
        const finalParams = {
            target: {
                "sms.supplier_id": supplierId,
                "sms.supplier_trunk_id": trunkIsActual ? supplierTrunkId : null,
                dialer_id_list: dialerIdList
            },
            ...restParams,
            senderid: restParams?.senderid ? restParams.senderid : "",
            comment: restParams?.comment ? restParams.comment : "",
            active: currentActive != null ? currentActive : rowData.active,
            permanent: currentRemoveInactive != null ? currentRemoveInactive : rowData.permanent,
            lock_to_reliable: currentLockToTrusted != null ? currentLockToTrusted : rowData.lock_to_reliable
        };

        const modal = modifyFormModel(params);
        const checkedForm = modal.check(params);
        const hasErrorData = hasErrorObject(checkedForm);

        if (hasErrorData) {
            return;
        }

        try {
            const response = await modifyApiFunc(finalParams);

            if (response && response.routing_key) {
                Alert.success("Routing modified!");
            }

            return response;
        } catch (error) {
            throw error;
        }
    };


    const handleRemoveException = async (key) => {
        try {
            const response = await removeApiFunc(key);


            if (response && response.routing_key) {
                Alert.success("Routing removed!");
            }

            return response;
        } catch (error) {
            throw error;
        }
    };

    const windowWidth = useWindowWidth();

    const mccMncMatchList = [
        {
            value: false,
            name: "Full match"
        },
        {
            value: true,
            name: "Not full match"
        }
    ];

    const exceptionList = [
        {
            value: "allowed",
            name: "Allowed"
        },
        {
            value: "blocked",
            name: "Blocked"
        },
        {
            value: "locked",
            name: "Locked"
        }
    ];

    return (
        <PanelLayout>
            <PageHeader title="Routing" />
            <RoutingFilter
                mccMncList={mccMncList}
                dialerList={dialerList}
                defaultFilter={defaultFilter}
                supplierList={supplierList}

                onChangeFilter={onChangeFilter}
                mccMncMatchList={mccMncMatchList}
                exceptionList={exceptionList}
            />
            <RoutingTable
                loading={loading}
                formModel={createFormModel}
                modifyFormModel={modifyFormModel}
                mccMncList={mccMncList}
                dialerList={dialerList}
                windowWidth={windowWidth}
                reportLoading={reportLoading}
                exceptionList={exceptionList}
                dataList={routingFilteredList}
                mccMncMatchList={mccMncMatchList}
                supplierList={supplierList}

                createFormDefaultData={createFormDefaultData}

                editKeys={editKeys}
                setEditKeys={setEditKeys}
                activeRoutes={activeRoutes}
                noAutoDeleteRoutes={noAutoDeleteRoutes}
                lockToTrustedRoutes={lockToTrustedRoutes}

                getList={getRoutingList}

                extraOnChange={(value) => {
                    setCreateState({ ...value });
                }}

                onDataUpdate={onDataUpdate}
                setActiveRoutes={setActiveRoutes}
                setNoAutoDeleteRoutes={setNoAutoDeleteRoutes}
                setLockToTrustedRoutes={setLockToTrustedRoutes}
                onRowCreate={handleCreateException}
                onRowSubmit={handleModifyException}
                onRowRemove={handleRemoveException}
                createPermission={checkPermissionsFor(ROUTING_CREATE_API)}
                modifyPermission={checkPermissionsFor(ROUTING_MODIFY_API)}
                removePermission={checkPermissionsFor(ROUTING_REMOVE_API)}
            />
        </PanelLayout>
    );
};

const mapStateToProps = ({ routing, references, suppliers, e212 }) => ({
    dialerList: references["sms.dialer_list"],
    supplierList: routing.supplierList,
    supplierAllManaged: references["sms.supplier_all_are_managed"],
    reportLoading: references.reportLoading,
    e212List: e212.list,
    routingList: routing.list,
    loading: routing.loading
});

const mapDispatchToProps = {
    getRoutingList,
    createApiFunc: onCreateRoutingItem,
    modifyApiFunc: onModifyRoutingItem,
    removeApiFunc: onRemoveRoutingItem,
    getE212DropdownList,
    getRoutingSupplierList,
    getReferencesForReports
};

export default connect(mapStateToProps, mapDispatchToProps)(RoutingPage);

